import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FormData {
  name: string;
  phone: string;
  email: string;
  property_address: string;
  service_required: string;
  move_date: string;
  details: string;
}

const initialForm: FormData = {
  name: "",
  phone: "",
  email: "",
  property_address: "",
  service_required: "",
  move_date: "",
  details: "",
};

const SERVICES = [
  "Residential Cleaning",
  "End Of Lease Cleaning",
  "Office Cleaning",
  "Window Cleaning",
  "Carpet Cleaning",
  "Pressure Washing",
  "Builders Cleans",
  "Airbnb Cleaning",
];

const QuoteForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (!/^[\d\s()+-]{7,20}$/.test(form.phone.trim()))
      e.phone = "Enter a valid phone number";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Enter a valid email";
    if (!form.property_address.trim()) e.property_address = "Property address is required";
    if (!form.service_required) e.service_required = "Please select a service";
    if (!form.move_date) e.move_date = "Preferred date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const { error } = await supabase.from("quote_requests").insert({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      property_address: form.property_address.trim(),
      service_required: form.service_required,
      move_date: form.move_date,
      details: form.details.trim() || null,
    });
    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
      return;
    }

    setSubmitted(true);
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3.5 border-2 rounded-md bg-white text-[#111111] font-medium placeholder:text-[#111111]/30 focus:border-[#0066FF] focus:outline-none transition-colors ${
      errors[field] ? "border-red-400" : "border-[#111111]/10"
    }`;

  return (
    <section id="quote" className="bg-[#f7f7f7] py-16 md:py-20">
      <div className="container max-w-2xl">
        <h2 className="text-3xl md:text-4xl font-black text-[#111111] text-center mb-3">
          Get Your Cleaning Quote In 60 Seconds
        </h2>
        <p className="text-center text-[#111111]/75 mb-6 text-base">
          Tell us a few details and we'll get back to you quickly with pricing and availability.
        </p>
        <p className="text-center text-xs font-semibold text-[#111111]/70 tracking-wide mb-10">
          No obligation &nbsp;•&nbsp; Fast response &nbsp;•&nbsp; Fully insured
        </p>

        {submitted ? (
          <div className="bg-[#e8f5e9] border-2 border-[#4caf50] rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-[#111111] mb-2">
              Thanks - we've received your quote request!
            </h3>
            <p className="text-[#111111]/60">
              We'll be in touch shortly with pricing and availability.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={inputClass("name")}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={inputClass("phone")}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>
            <div>
              <input
                type="email"
                placeholder="Email Address"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={inputClass("email")}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <input
                type="text"
                placeholder="Property Address"
                value={form.property_address}
                onChange={(e) => handleChange("property_address", e.target.value)}
                className={inputClass("property_address")}
              />
              {errors.property_address && <p className="text-red-500 text-xs mt-1">{errors.property_address}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <select
                  aria-label="Service required"
                  value={form.service_required}
                  onChange={(e) => handleChange("service_required", e.target.value)}
                  className={inputClass("service_required") + (form.service_required ? "" : " text-[#111111]/40")}
                >
                  <option value="">Service Required</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="text-[#111111]">{s}</option>
                  ))}
                </select>
                {errors.service_required && <p className="text-red-500 text-xs mt-1">{errors.service_required}</p>}
              </div>
              <div>
                <input
                  type="date"
                  aria-label="Preferred date"
                  value={form.move_date}
                  onChange={(e) => handleChange("move_date", e.target.value)}
                  className={inputClass("move_date")}
                />
                {errors.move_date && <p className="text-red-500 text-xs mt-1">{errors.move_date}</p>}
              </div>
            </div>
            <textarea
              placeholder="Additional notes (optional)"
              aria-label="Additional notes"
              rows={3}
              value={form.details}
              onChange={(e) => handleChange("details", e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-[#111111]/10 rounded-md bg-white text-[#111111] font-medium placeholder:text-[#111111]/30 focus:border-[#0066FF] focus:outline-none transition-colors resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0066FF] text-white py-4 text-lg font-bold rounded-md hover:bg-[#0066FF]/90 transition-colors disabled:opacity-60 shadow-[0_4px_14px_rgba(0,102,255,0.3)]"
            >
              {loading ? "Submitting…" : "Get My Free Quote"}
            </button>
            <p className="text-center text-xs text-[#111111]/70">
              No obligation &nbsp;•&nbsp; Fast response &nbsp;•&nbsp; Fully insured
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default QuoteForm;
